import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Award, Lock, Share2, X, Zap, Gauge, Flame, Scale, Crown, Drama, Mic, Megaphone as MegaIcon,
  Repeat, Sparkles, UserPlus, Radio, Link as LinkIcon, Shuffle, Shield, Target, Eye, Layers, CalendarCheck,
} from "lucide-react";

const ICONS = {
  needle_nudger: Zap, meter_mover: Gauge, vote_streaker: Flame, the_decider: Scale, ballot_baron: Crown,
  devils_advocate: Drama, mic_drop: Mic, the_instigator: Radio, switch_hitter: Repeat, the_spark: Sparkles,
  megaphone: MegaIcon, the_recruiter: UserPlus, echo_breaker: LinkIcon, bridge_builder: Layers,
  flip_flopper: Shuffle, underdog_champion: Shield, tipping_point: Target, the_oracle: Eye,
  jack_of_all_sides: Layers, daily_flipper: CalendarCheck,
};
const GROUP_COLOR = { Voting: "#7B2CBF", Debate: "#9D4EDD", Sharing: "#16A34A", Wildcard: "#FFC300" };
const GROUPS = ["Voting", "Debate", "Sharing", "Wildcard"];

function BadgeTile({ b, onOpen }) {
  const Icon = ICONS[b.id] || Award;
  const color = GROUP_COLOR[b.group];
  const pct = b.target ? Math.round((b.progress / b.target) * 100) : 0;
  return (
    <motion.button
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.96 }}
      onClick={() => onOpen(b)}
      className={`relative flex flex-col items-center gap-1.5 p-3 rounded-xl border text-center transition-colors ${
        b.earned ? "bg-white border-gray-100 shadow-card" : "bg-gray-50 border-dashed border-gray-200"
      }`}
      data-testid={`badge-${b.id}`}
    >
      <span
        className="w-11 h-11 rounded-full flex items-center justify-center"
        style={b.earned ? { background: `${color}1A`, color } : { background: "#E2E8F0", color: "#94A3B8" }}
      >
        {b.earned ? <Icon className="w-5 h-5" /> : <Lock className="w-4 h-4" />}
      </span>
      <span className={`text-[11px] font-semibold leading-tight ${b.earned ? "text-dark" : "text-neutral"}`}>{b.name}</span>
      {!b.earned && b.target > 1 && (
        <span className="w-full h-1 bg-gray-200 rounded-full overflow-hidden">
          <span className="block h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
        </span>
      )}
      {b.earned && <motion.span className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center" style={{ background: color }} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 300, damping: 15 }}><Award className="w-2.5 h-2.5 text-white" /></motion.span>}
    </motion.button>
  );
}

function BadgeModal({ b, profileName, onClose }) {
  const Icon = ICONS[b.id] || Award;
  const color = GROUP_COLOR[b.group];
  const url = window.location.href.split("?")[0];
  const caption = `${profileName} just earned the "${b.name}" badge on Flipped View — ${b.desc}`;
  const share = async () => {
    try {
      if (navigator.share) await navigator.share({ title: b.name, text: caption, url });
      else { await navigator.clipboard.writeText(`${caption} ${url}`); toast.success("Badge caption copied!"); }
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("Could not share right now");
    }
  };
  const xUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}&url=${encodeURIComponent(url)}`;
  return (
    <motion.div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-dark/60 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-testid="badge-modal">
      <motion.div
        initial={{ scale: 0.85, y: 20, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 22 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-sm bg-white rounded-3xl p-6 text-center shadow-card-hover"
      >
        <button onClick={onClose} className="absolute top-3 right-3 p-1.5 text-neutral hover:text-dark rounded-lg hover:bg-gray-50" data-testid="badge-modal-close"><X className="w-4 h-4" /></button>
        <motion.span
          className="mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-3"
          style={b.earned ? { background: `${color}1A`, color } : { background: "#E2E8F0", color: "#94A3B8" }}
          animate={b.earned ? { rotate: [0, -6, 6, 0], scale: [1, 1.08, 1] } : {}}
          transition={{ duration: 0.8 }}
        >
          {b.earned ? <Icon className="w-9 h-9" /> : <Lock className="w-7 h-7" />}
        </motion.span>
        <span className="text-[10px] font-bold tracking-wider uppercase" style={{ color }}>{b.group} badge</span>
        <h3 className="font-heading text-3xl text-dark mt-1" data-testid="badge-modal-name">{b.name}</h3>
        <p className="text-sm text-neutral mt-2 leading-relaxed">{b.desc}</p>
        {!b.earned && <p className="mt-3 text-xs font-semibold text-dark bg-gray-50 rounded-lg py-2" data-testid="badge-modal-progress">Progress: {b.progress} / {b.target}</p>}
        {b.earned && (
          <div className="flex items-center justify-center gap-2 mt-5">
            <button onClick={share} className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-full" style={{ background: color }} data-testid="badge-share-btn">
              <Share2 className="w-3.5 h-3.5" /> Share badge
            </button>
            <a href={xUrl} target="_blank" rel="noreferrer" className="px-4 py-2 text-xs font-semibold text-dark bg-gray-100 rounded-full hover:bg-dark hover:text-white transition-colors" data-testid="badge-share-x-btn">Post on X</a>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

export default function BadgeGrid({ badges = [], badgeCount = 0, profileName }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="bg-white rounded-2xl shadow-card p-5 mb-4" data-testid="badge-grid">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl text-dark flex items-center gap-2"><Award className="w-5 h-5 text-amber-500" /> FLIP BADGES</h2>
        <span className="text-xs font-semibold text-neutral" data-testid="badge-count">{badgeCount} / {badges.length} earned</span>
      </div>
      <div className="space-y-5">
        {GROUPS.map((g) => (
          <div key={g}>
            <h4 className="text-[10px] font-bold tracking-wider uppercase mb-2" style={{ color: GROUP_COLOR[g] }}>{g}</h4>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {badges.filter((b) => b.group === g).map((b) => <BadgeTile key={b.id} b={b} onOpen={setOpen} />)}
            </div>
          </div>
        ))}
      </div>
      <AnimatePresence>{open && <BadgeModal b={open} profileName={profileName} onClose={() => setOpen(null)} />}</AnimatePresence>
    </div>
  );
}
